import React, { useEffect, useRef, useState } from "react";
import { Monitor, Scaling, Smartphone, Tablet, TriangleAlert } from "lucide-react";

// Simulated viewport widths for the page editor's Content canvas.
export const VIEWPORT_PRESETS = [
  { id: "mobile", label: "Mobile", width: 360, Icon: Smartphone },
  { id: "tablet", label: "Tablet", width: 768, Icon: Tablet },
  { id: "desktop", label: "Desktop", width: 1280, Icon: Monitor },
] as const;

export const DEFAULT_VIEWPORT_WIDTH = 1280;
const MIN_VIEWPORT_WIDTH = 320;
const MAX_VIEWPORT_WIDTH = 3840;
// Horizontal padding the frame container reserves for the drag handle (px-2).
const FRAME_GUTTER = 16;

function clampWidth(value: number): number {
  return Math.min(Math.max(Math.round(value), MIN_VIEWPORT_WIDTH), MAX_VIEWPORT_WIDTH);
}

interface ViewAsControlProps {
  width: number;
  onChange: (width: number) => void;
  autoScale: boolean;
  onAutoScaleChange: (autoScale: boolean) => void;
}

/**
 * "View as" control for the tabs bar: device presets plus an editable width
 * box and the auto-scale toggle. A preset highlights only while the width
 * equals its value, so any custom width (typed or dragged) deselects it. The
 * width box commits on Enter/blur so partially-typed numbers don't resize the
 * canvas mid-keystroke.
 */
export function ViewAsControl({ width, onChange, autoScale, onAutoScaleChange }: ViewAsControlProps) {
  const [draft, setDraft] = useState(String(width));
  useEffect(() => setDraft(String(width)), [width]);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(width));
    } else {
      onChange(clampWidth(parsed));
    }
  };

  return (
    <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1">
      {VIEWPORT_PRESETS.map(({ id, label, width: presetWidth, Icon }) => (
        <button
          key={id}
          type="button"
          title={`${label} (${presetWidth}px)`}
          aria-label={`View as ${label.toLowerCase()}`}
          aria-pressed={width === presetWidth}
          onClick={() => onChange(presetWidth)}
          className={`inline-flex items-center justify-center rounded-md px-2.5 py-1.5 transition-colors ${
            width === presetWidth
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        aria-label="Viewport width in pixels"
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit();
            e.currentTarget.blur();
          }
        }}
        className="ml-0.5 w-14 rounded-md border-0 bg-background px-1.5 py-1.5 text-center text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
      <button
        type="button"
        title="Auto scale: shrink the preview to fit when the viewport is wider than the editor"
        aria-label="Toggle auto scale"
        aria-pressed={autoScale}
        onClick={() => onAutoScaleChange(!autoScale)}
        className={`inline-flex items-center justify-center rounded-md px-2.5 py-1.5 transition-colors ${
          autoScale
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <Scaling className="h-4 w-4" />
      </button>
    </div>
  );
}

interface ViewportFrameProps {
  width: number;
  autoScale: boolean;
  onWidthChange: (width: number) => void;
  children: React.ReactNode;
}

/**
 * Centred, rounded frame that constrains the canvas to the simulated viewport
 * width. The right edge is a drag zone: a grip pill follows the cursor along
 * the edge (ew-resize cursor) and dragging left/right resizes the viewport.
 *
 * When the viewport is wider than the editor pane: with auto-scale on, the
 * frame shrinks visually via `zoom` (its CSS width — and therefore the
 * canvas's container queries — still see the full simulated width); with
 * auto-scale off it renders at 100% behind a horizontal scrollbar and a
 * warning is shown.
 */
export function ViewportFrame({ width, autoScale, onWidthChange, children }: ViewportFrameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [handleY, setHandleY] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [paneWidth, setPaneWidth] = useState<number | null>(null);

  // Track the pane width (for fit-scale and the overflow warning).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setPaneWidth(el.clientWidth - FRAME_GUTTER);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const exceedsPane = paneWidth !== null && width > paneWidth;
  const scale = autoScale && exceedsPane ? paneWidth / width : 1;
  const showOverflowWarning = !autoScale && exceedsPane;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Capture is an enhancement (keeps the drag when the pointer leaves the
      // zone); the drag still works without it.
    }
    dragRef.current = { startX: e.clientX, startWidth: width };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const frame = frameRef.current?.getBoundingClientRect();
    if (frame) {
      // The rect is in visual (scaled) pixels; the pill is positioned in the
      // frame's own CSS pixels, so convert.
      const y = (e.clientY - frame.top) / scale;
      setHandleY(Math.min(Math.max(y, 16), frame.height / scale - 16));
    }
    const drag = dragRef.current;
    if (!drag) return;
    // Pointer deltas are visual pixels → divide by scale for CSS pixels.
    // While narrower than the pane the frame is centred, so the right edge
    // moves by half of any width change (×2 keeps it under the pointer);
    // once it overflows (scroll or fit-scale) the edges are pinned (×1).
    const factor = (paneWidth === null || width < paneWidth - 1 ? 2 : 1) / scale;
    const next = drag.startWidth + (e.clientX - drag.startX) * factor;
    onWidthChange(clampWidth(next));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      if (dragRef.current) e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Nothing to release when capture wasn't acquired.
    }
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <div>
      {showOverflowWarning && (
        <p className="mb-3 flex items-center justify-center gap-1.5 text-xs text-warning-foreground">
          <TriangleAlert className="h-3.5 w-3.5" />
          The viewport ({width}px) is wider than the editor — scroll sideways, or turn on auto scale.
        </p>
      )}

      {/* The frame keeps its true CSS width so container queries in the canvas
          see the simulated viewport; when it exceeds the pane it either
          fit-scales (zoom) or scrolls horizontally (px-2 leaves room for the
          drag handle). */}
      <div ref={containerRef} className={`overflow-x-auto px-2 ${dragging ? "select-none" : ""}`}>
        <div
          ref={frameRef}
          className="relative mx-auto"
          style={{ width, zoom: scale !== 1 ? scale : undefined }}
        >
          <div className="overflow-hidden rounded-lg border">{children}</div>

          {/* Right-edge drag zone; the grip pill tracks the cursor's height. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Drag to resize viewport"
            className="group/handle absolute -right-2 inset-y-0 w-4 cursor-ew-resize touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={() => { if (!dragRef.current) setHandleY(null); }}
          >
            {handleY !== null && (
              <div
                aria-hidden="true"
                className={`absolute left-1 h-8 w-1.5 -translate-y-1/2 rounded-full transition-colors ${
                  dragging ? "bg-primary" : "bg-muted-foreground/60 group-hover/handle:bg-muted-foreground"
                }`}
                style={{ top: handleY }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
